// ── Active systems on the live map: one clickable NHC-style symbol each ──
// The front door of the live view: see a storm, click it, its forecast,
// guidance and history load. Positions come from /api/active (the newest
// best-track fix), so every system shows without loading its full bundle.

import { IconLayer, TextLayer } from "@deck.gl/layers";
import type { GtwoFeature, StormRef } from "../data/liveApi";
import {
  ageText, fixAgeHours, formationOdds, motionText, STALE_FIX_H, systemClass, systemName, type SystemKind,
} from "../engine/liveSystems";
import { nhcAreaColor } from "../features/GenesisWatchPanel";
import { ssColor } from "../features/IntensityTrend";
import { useStore } from "../state/store";
import { systemIconSize, systemIconUrl } from "./stormIcons";

export interface SystemPin {
  sys: true; // tooltip discriminator
  id: string;
  lon: number;
  lat: number;
  kind: SystemKind;
  name: string;
  classLabel: string; // "Major Hurricane · Cat 4"
  color: string;
  icon: string;
  size: number;
  label: string; // text under the symbol
  vmax: number | null;
  mslp: number | null;
  dv24: number | null;
  motion: string | null;
  ageH: number | null;
  odds: { prob2: number | null; prob7: number | null } | null;
}

/** Symbol colour: Saffir-Simpson for cyclones, NHC's 7-day odds colour for
 *  invests (neutral when the outlook has no match), grey once post-tropical. */
export function systemColor(kind: SystemKind, vmax: number | null, prob7: number | null | undefined): string {
  if (kind === "invest") return prob7 != null ? nhcAreaColor(prob7, null) : "#CBD5E1";
  if (kind === "post") return "#94A3B8";
  return ssColor(vmax ?? 0);
}

/** Pins for every active system with a known position, plus the NHC ✕
 *  markers they matched (the map drops those duplicates). */
export function buildSystemPins(index: StormRef[], gtwo: GtwoFeature[] | undefined, now = Date.now()) {
  const matched = new Set<GtwoFeature>();
  const pins: SystemPin[] = [];
  for (const ref of index) {
    const f = ref.latest;
    if (!f) continue;
    const cls = systemClass(ref);
    const odds = formationOdds(ref, gtwo);
    if (odds?.point) matched.add(odds.point);
    const color = systemColor(cls.kind, f.vmax, odds?.prob7);
    const name = systemName(ref);
    pins.push({
      sys: true, id: ref.storm_id, lon: f.lon, lat: f.lat, kind: cls.kind, name,
      classLabel: cls.cat ? `${cls.label} · Cat ${cls.cat}` : cls.label,
      color, icon: systemIconUrl(cls.kind, color), size: systemIconSize(cls.kind),
      label: cls.kind === "invest"
        ? `${name}${odds?.prob7 != null ? ` · ${odds.prob7}%` : ""}`
        : `${name} · ${f.vmax ?? "?"} kt`,
      vmax: f.vmax, mslp: f.mslp, dv24: f.dv24, motion: motionText(ref),
      ageH: fixAgeHours(ref, now),
      odds: odds ? { prob2: odds.prob2, prob7: odds.prob7 } : null,
    });
  }
  return { pins, matched };
}

type Box = [number, number, number, number]; // x0, y0, x1, y1 (px)
export type LabelSpot = "below" | "above" | "right" | "left";
const SPOTS: LabelSpot[] = ["below", "above", "right", "left"];
const overlaps = (a: Box, b: Box) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
const labelPriority = (p: SystemPin) => (p.kind === "invest" ? (p.odds?.prob7 ?? 0) - 200 : p.vmax ?? 0);

function spotBox(spot: LabelSpot, x: number, y: number, size: number, w: number): Box {
  const h = 15, r = size / 2;
  if (spot === "below") return [x - w / 2, y + r + 1, x + w / 2, y + r + 1 + h];
  if (spot === "above") return [x - w / 2, y - r - 1 - h, x + w / 2, y - r - 1];
  if (spot === "right") return [x + r + 3, y - h / 2, x + r + 3 + w, y + h / 2];
  return [x - r - 3 - w, y - h / 2, x - r - 3, y + h / 2];
}

/** Where each pin's label goes at the current view: strongest system first,
 *  each trying below / above / right / left of its symbol, never over a label
 *  already placed or another symbol. A label with no free spot is dropped
 *  until you zoom in; symbols always show. */
export function declutterLabels(
  pins: SystemPin[], project: (p: SystemPin) => [number, number] | null,
): Map<string, LabelSpot> {
  const at = new Map(pins.map((p) => [p.id, project(p)]));
  const icons: [string, Box][] = [];
  for (const p of pins) {
    const xy = at.get(p.id);
    if (xy) icons.push([p.id, [xy[0] - p.size / 2, xy[1] - p.size / 2, xy[0] + p.size / 2, xy[1] + p.size / 2]]);
  }
  const out = new Map<string, LabelSpot>();
  const placed: Box[] = [];
  for (const p of [...pins].sort((a, b) => labelPriority(b) - labelPriority(a))) {
    const xy = at.get(p.id);
    if (!xy) continue;
    const w = p.label.length * 6.8 + 6; // ~12 px Inter bold
    for (const spot of SPOTS) {
      const box = spotBox(spot, xy[0], xy[1], p.size, w);
      if (placed.some((b) => overlaps(b, box)) || icons.some(([id, b]) => id !== p.id && overlaps(b, box))) continue;
      out.set(p.id, spot);
      placed.push(box);
      break;
    }
  }
  return out;
}

/** `spots`: where declutterLabels put each label for the current view (all
 *  below when null). */
export function buildSystemLayers(pins: SystemPin[], selectedId: string | null, spots: Map<string, LabelSpot> | null) {
  // the open storm draws its own position; the rest stay one click away
  const data = pins.filter((p) => p.id !== selectedId);
  if (!data.length) return [];
  const labels = spots ? data.filter((p) => spots.has(p.id)) : data;
  const spot = (d: SystemPin): LabelSpot => spots?.get(d.id) ?? "below";
  const open = (info: { object?: SystemPin | null }) => {
    if (info.object) void useStore.getState().selectLiveStorm(info.object.id);
    return true; // handled — not a climatology-track focus click
  };
  const opacity = selectedId ? 0.75 : 1;
  const alpha = (p: SystemPin) => (p.ageH != null && p.ageH > STALE_FIX_H ? 120 : 255);
  return [
    new IconLayer({
      id: "live-systems", data, pickable: true, opacity, onClick: open,
      getPosition: (d: SystemPin) => [d.lon, d.lat],
      getIcon: (d: SystemPin) => ({ url: d.icon, id: `${d.kind}|${d.color}`, width: 64, height: 64 }),
      getSize: (d: SystemPin) => d.size, sizeUnits: "pixels",
      getColor: (d: SystemPin) => [255, 255, 255, alpha(d)] as [number, number, number, number],
    }),
    new TextLayer({
      id: "live-system-labels", data: labels, pickable: true, opacity, onClick: open,
      getPosition: (d: SystemPin) => [d.lon, d.lat],
      getText: (d: SystemPin) => d.label,
      getPixelOffset: (d: SystemPin) => {
        const s = spot(d), r = d.size / 2;
        return s === "below" ? [0, r + 1] : s === "above" ? [0, -r - 1] : s === "right" ? [r + 3, 0] : [-r - 3, 0];
      },
      getSize: 12, sizeUnits: "pixels",
      getColor: (d: SystemPin) => [236, 242, 250, alpha(d)] as [number, number, number, number],
      getTextAnchor: (d: SystemPin) => (spot(d) === "right" ? "start" : spot(d) === "left" ? "end" : "middle"),
      getAlignmentBaseline: (d: SystemPin) => (spot(d) === "below" ? "top" : spot(d) === "above" ? "bottom" : "center"),
      fontFamily: "Inter, sans-serif", fontWeight: 700, characterSet: "auto",
      fontSettings: { sdf: true }, outlineWidth: 3, outlineColor: [11, 18, 32, 235],
    }),
  ];
}

export function systemTooltip(p: SystemPin): string {
  const lines = [`<b>${p.name}</b> · ${p.classLabel}`];
  if (p.kind === "potential") {
    lines.push('<span style="color:#FBBF24">Not yet a tropical cyclone: NHC advises on it before it forms —<br/>watches or warnings may be in effect</span>');
  }
  if (p.kind === "invest") {
    lines.push(p.odds
      ? `NHC formation chance: 48 h <b>${p.odds.prob2 ?? "?"}%</b> · 7 day <b>${p.odds.prob7 ?? "?"}%</b>`
      : "No matching NHC outlook area");
  }
  const obs = [p.vmax != null ? `${p.vmax} kt` : null, p.mslp != null ? `${p.mslp} mb` : null].filter(Boolean).join(" · ");
  if (obs) {
    // red = strengthening, like the storm panel's trend tiles
    const trend = p.dv24
      ? ` · <span style="color:${p.dv24 > 0 ? "#F87171" : "#4ADE80"}">${p.dv24 > 0 ? "▲" : "▼"}${Math.abs(p.dv24)} kt / 24 h</span>` +
        (p.dv24 >= 30 ? ' <b style="color:#F87171">RAPID INTENSIFICATION</b>' : "")
      : "";
    lines.push(obs + trend);
  }
  if (p.motion) lines.push(p.motion);
  if (p.ageH != null) {
    lines.push(`Last fix ${ageText(p.ageH)} ago${p.ageH > STALE_FIX_H ? ' <span style="color:#FBBF24">(stale)</span>' : ""}`);
  }
  lines.push('<span style="color:#60A5FA">Click to open ›</span>');
  return `<div style="line-height:1.45">${lines.join("<br/>")}</div>`;
}
